import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  LinearProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  CheckCircle as CheckIcon,
  PendingActions as PendingIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  DynamicForm as DynamicFormIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import config from '../../../config';

/**
 * Dashboard principal del módulo Admin
 * Muestra estadísticas clave y accesos rápidos
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stats, setStats] = useState({
    empresasActivas: 0,
    empresasTotal: 0,
    usuariosTotales: 0,
    negociosPendientes: 0,
    plantillasCreadas: 0,
    camposGlobales: 0,
    empresasConPlantilla: 0
  });

  const [loading, setLoading] = useState(true);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(new Date());

  /**
   * Cargar estadísticas desde el backend
   */
  const loadStats = async () => {
    try {
      setLoading(true);

      // Llamadas paralelas para mejor rendimiento
      const [empresasRes, usuariosRes, plantillasRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/api/empresas`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        axios.get(`${config.API_BASE_URL}/api/usuarios`, {
          headers: { Authorization: `Bearer ${user.token}` }
        }),
        axios.get(`${config.API_BASE_URL}/api/v1/plantillas`, {
          headers: { Authorization: `Bearer ${user.token}` }
        })
      ]);

      const empresas = empresasRes.data || [];
      const usuarios = usuariosRes.data || [];
      const plantillas = plantillasRes.data?.data || [];

      setStats({
        empresasActivas: empresas.filter(e => e.estado === 'activo').length,
        empresasTotal: empresas.length,
        usuariosTotales: usuarios.length,
        negociosPendientes: empresas.filter(e => e.estado === 'pendiente').length,
        plantillasCreadas: plantillas.length,
        camposGlobales: 0, // TODO: agregar endpoint
        empresasConPlantilla: empresas.filter(e => e.id_plantilla).length
      });

      setUltimaActualizacion(new Date());
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.token) {
      loadStats();
    }
  }, [user]);

  /**
   * Calcular porcentaje de empresas con plantilla
   */
  const porcentajeConPlantilla = stats.empresasTotal > 0
    ? Math.round((stats.empresasConPlantilla / stats.empresasTotal) * 100)
    : 0;

  return (
    <Box>
      {/* Header con gradiente TRACKEA */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
        borderRadius: 3,
        p: 4,
        mb: 4,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box position="relative" zIndex={1}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Panel de Administración
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Gestión centralizada del sistema TRACKEA
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7, mt: 1, display: 'block' }}>
                Última actualización: {ultimaActualizacion.toLocaleTimeString('es-PE')}
              </Typography>
            </Box>
            <Tooltip title="Actualizar datos">
              <IconButton
                onClick={loadStats}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Decoración de fondo */}
        <Box sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
          opacity: 0.5
        }} />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        {/* Card 1: Empresas Activas */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              borderRadius: 3,
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(26, 35, 126, 0.15)'
              }
            }}
            onClick={() => navigate('/admin/sistema/empresas')}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" fontWeight={600}>
                    EMPRESAS ACTIVAS
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="#1A237E">
                    {loading ? '...' : stats.empresasActivas}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    de {stats.empresasTotal} totales
                  </Typography>
                </Box>
                <Avatar sx={{
                  bgcolor: '#4FC3F7',
                  width: 56,
                  height: 56,
                  boxShadow: '0 4px 14px rgba(79, 195, 247, 0.3)'
                }}>
                  <BusinessIcon fontSize="large" />
                </Avatar>
              </Box>
              <LinearProgress
                variant="determinate"
                value={stats.empresasTotal > 0 ? (stats.empresasActivas / stats.empresasTotal) * 100 : 0}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(79, 195, 247, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #4FC3F7 0%, #29B6F6 100%)'
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Card 2: Usuarios */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              borderRadius: 3,
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(102, 187, 106, 0.15)'
              }
            }}
            onClick={() => navigate('/admin/sistema/usuarios')}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" fontWeight={600}>
                    USUARIOS REGISTRADOS
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="#66BB6A">
                    {loading ? '...' : stats.usuariosTotales}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    usuarios activos
                  </Typography>
                </Box>
                <Avatar sx={{
                  bgcolor: '#66BB6A',
                  width: 56,
                  height: 56,
                  boxShadow: '0 4px 14px rgba(102, 187, 106, 0.3)'
                }}>
                  <PeopleIcon fontSize="large" />
                </Avatar>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUpIcon sx={{ color: '#66BB6A', fontSize: 16 }} />
                <Typography variant="caption" color="#66BB6A" fontWeight={600}>
                  Sistema operativo
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 3: Negocios Pendientes */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              borderRadius: 3,
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(255, 167, 38, 0.15)'
              }
            }}
            onClick={() => navigate('/admin/sistema/negocios/aprobar')}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" fontWeight={600}>
                    NEGOCIOS PENDIENTES
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="#FFA726">
                    {loading ? '...' : stats.negociosPendientes}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    requieren aprobación
                  </Typography>
                </Box>
                <Avatar sx={{
                  bgcolor: '#FFA726',
                  width: 56,
                  height: 56,
                  boxShadow: '0 4px 14px rgba(255, 167, 38, 0.3)'
                }}>
                  <PendingIcon fontSize="large" />
                </Avatar>
              </Box>
              {stats.negociosPendientes > 0 && (
                <Chip
                  label="Acción requerida"
                  size="small"
                  color="warning"
                  icon={<PendingIcon />}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card 4: Plantillas V1 */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={3}
            sx={{
              borderRadius: 3,
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(26, 35, 126, 0.15)'
              }
            }}
            onClick={() => navigate('/admin/configuracion/plantillas')}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2" fontWeight={600}>
                    PLANTILLAS CREADAS
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="#1A237E">
                    {loading ? '...' : stats.plantillasCreadas}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    plantillas disponibles
                  </Typography>
                </Box>
                <Avatar sx={{
                  bgcolor: '#1A237E',
                  width: 56,
                  height: 56,
                  boxShadow: '0 4px 14px rgba(26, 35, 126, 0.3)'
                }}>
                  <AssessmentIcon fontSize="large" />
                </Avatar>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckIcon sx={{ color: '#1A237E', fontSize: 16 }} />
                <Typography variant="caption" color="#1A237E" fontWeight={600}>
                  Configuración V1
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sección de Configuración V1 */}
      <Grid container spacing={3}>
        {/* Progreso de Adopción V1 */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <DynamicFormIcon sx={{ color: '#1A237E', fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Adopción de Configuración V1
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box mb={4}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">
                  Empresas con Plantilla Aplicada
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats.empresasConPlantilla} / {stats.empresasTotal}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={porcentajeConPlantilla}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: 'rgba(26, 35, 126, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    background: 'linear-gradient(90deg, #1A237E 0%, #3949AB 100%)'
                  }
                }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {porcentajeConPlantilla}% de empresas configuradas
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box textAlign="center" p={2} bgcolor="#F5F5F5" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="#1A237E">
                    {stats.plantillasCreadas}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Plantillas
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center" p={2} bgcolor="#F5F5F5" borderRadius={2}>
                  <Typography variant="h4" fontWeight="bold" color="#4FC3F7">
                    {stats.camposGlobales}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Campos Globales
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Accesos Rápidos */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
              Accesos Rápidos
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: 'rgba(79, 195, 247, 0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(79, 195, 247, 0.2)',
                      transform: 'scale(1.02)'
                    }
                  }}
                  onClick={() => navigate('/admin/configuracion/empresas')}
                >
                  <CardContent>
                    <BusinessIcon sx={{ color: '#4FC3F7', fontSize: 40, mb: 1 }} />
                    <Typography variant="body1" fontWeight="bold">
                      Configurar Empresas
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Aplicar plantillas y personalizar
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: 'rgba(26, 35, 126, 0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(26, 35, 126, 0.2)',
                      transform: 'scale(1.02)'
                    }
                  }}
                  onClick={() => navigate('/admin/configuracion/plantillas')}
                >
                  <CardContent>
                    <AssessmentIcon sx={{ color: '#1A237E', fontSize: 40, mb: 1 }} />
                    <Typography variant="body1" fontWeight="bold">
                      Gestionar Plantillas
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Crear y editar plantillas
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: 'rgba(102, 187, 106, 0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(102, 187, 106, 0.2)',
                      transform: 'scale(1.02)'
                    }
                  }}
                  onClick={() => navigate('/admin/configuracion/campos')}
                >
                  <CardContent>
                    <DynamicFormIcon sx={{ color: '#66BB6A', fontSize: 40, mb: 1 }} />
                    <Typography variant="body1" fontWeight="bold">
                      Catálogo de Campos
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Gestionar campos dinámicos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: 'rgba(255, 167, 38, 0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(255, 167, 38, 0.2)',
                      transform: 'scale(1.02)'
                    }
                  }}
                  onClick={() => navigate('/admin/sistema/empresas/crear')}
                >
                  <CardContent>
                    <PeopleIcon sx={{ color: '#FFA726', fontSize: 40, mb: 1 }} />
                    <Typography variant="body1" fontWeight="bold">
                      Nueva Empresa
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Registrar empresa al sistema
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
